import { Fragment } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import {
  Breadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { ID_LIKE_SEGMENT, STATIC_LABELS } from "./header-breadcrumbs/constants";
import { useBreadcrumbData } from "./header-breadcrumbs/hooks";
import {
  createDynamicIds,
  extractIds,
} from "./header-breadcrumbs/id-extractors";
import {
  createDynamicLabels,
  type DynamicLabelMapping,
} from "./header-breadcrumbs/label-generator";
import type {
  BreadcrumbItemData,
  HeaderBreadcrumbsProps,
} from "./header-breadcrumbs/types";
import { toTitle } from "./header-breadcrumbs/utils";

export function HeaderBreadcrumbs({
  dashboardPath,
  className,
}: HeaderBreadcrumbsProps) {
  const location = useLocation();
  const params = useParams<{
    id?: string;
    siteId?: string;
    entranceId?: string;
    templateId?: string;
    formId?: string;
    signInId?: string;
  }>();

  const segments = location.pathname.split("/").filter(Boolean);

  const extractedIds = extractIds(segments, params);
  const dynamicIds = createDynamicIds(extractedIds);

  const { site, worker, entrance, template, form, signIn } =
    useBreadcrumbData(dynamicIds);

  const dynamicLabelMappings: DynamicLabelMapping[] = [
    { idKey: dynamicIds.site || "", queryResult: site, labelKeys: ["name"] },
    {
      idKey: dynamicIds.worker || "",
      queryResult: worker,
      labelKeys: ["fullName"],
    },
    {
      idKey: dynamicIds.entrance || "",
      queryResult: entrance,
      labelKeys: ["name"],
    },
    {
      idKey: dynamicIds.template || "",
      queryResult: template,
      labelKeys: ["template.name"],
    },
    {
      idKey: dynamicIds.form || "",
      queryResult: form,
      labelKeys: ["form.data.meta.title", "template.name"],
    },
    {
      idKey: dynamicIds.signIn || "",
      queryResult: signIn,
      labelKeys: ["workerName"],
    },
  ];

  const dynamicLabels = createDynamicLabels(dynamicLabelMappings);

  const breadcrumbItems: BreadcrumbItemData[] = [];
  const isOnDashboard = location.pathname === dashboardPath;

  breadcrumbItems.push({
    segment: "dashboard",
    label: "Dashboard",
    href: isOnDashboard ? undefined : dashboardPath,
  });

  segments.forEach((segment, index) => {
    if (index === 0 && segment === "dashboard") {
      return;
    }

    const label =
      dynamicLabels[segment] ?? STATIC_LABELS[segment] ?? toTitle(segment);

    const isLast = index === segments.length - 1;
    const isDynamic =
      Boolean(dynamicLabels[segment]) || ID_LIKE_SEGMENT.test(segment);

    const isSiteSegment =
      dynamicLabels[segment] && dynamicIds.site === segment && site.data?.id;
    const isEntranceSegment =
      dynamicLabels[segment] &&
      dynamicIds.entrance === segment &&
      entrance.data?.id &&
      dynamicIds.site;
    const isWorkerSegment =
      dynamicLabels[segment] &&
      dynamicIds.worker === segment &&
      worker.data?.id;

    // Make site name clickable to launch pad if dynamic label and segment is a site id
    let href: string | undefined;
    if (!isLast && !isDynamic) {
      href = `/${segments.slice(0, index + 1).join("/")}`;
    } else if (isSiteSegment && site.data?.id) {
      href = `/sites/${site.data.id}`;
    } else if (isEntranceSegment) {
      href = `/sites/${dynamicIds.site}/entrances`;
    } else if (isWorkerSegment && worker.data?.id) {
      href = `/workers/${worker.data.id}`;
    }

    breadcrumbItems.push({ segment, label, href });
  });

  const linkClassName =
    "max-w-28 truncate text-sm font-medium text-muted-foreground hover:text-foreground md:max-w-none";
  const separatorClassName = "text-muted-foreground/50";
  const pageClassName =
    "max-w-40 truncate text-sm font-semibold text-foreground md:max-w-none";
  const listClassName =
    "flex-nowrap overflow-hidden whitespace-nowrap text-muted-foreground text-xs md:text-sm";

  const shouldCollapse = breadcrumbItems.length > 3;
  const visibleItems = shouldCollapse
    ? breadcrumbItems.slice(-2)
    : breadcrumbItems;
  const collapsedItems = shouldCollapse ? breadcrumbItems.slice(0, -2) : [];

  return (
    <div className={cn("w-full min-w-0", className)}>
      <Breadcrumb>
        {shouldCollapse && collapsedItems.length > 0 && (
          <div data-slot="mobile-breadcrumbs" className="md:hidden">
            <BreadcrumbList className={`${listClassName} flex`}>
              <BreadcrumbItem className="shrink-0">
                <DropdownMenu>
                  <DropdownMenuTrigger className="flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground">
                    <BreadcrumbEllipsis className="size-4" />
                    <span className="sr-only">Toggle menu</span>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    {collapsedItems.map((collapsedItem, collapsedIndex) => (
                      <DropdownMenuItem
                        key={`${collapsedItem.segment}-${collapsedIndex}`}
                        asChild
                      >
                        <Link to={collapsedItem.href || "#"}>
                          {collapsedItem.label}
                        </Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </BreadcrumbItem>
              <BreadcrumbSeparator className={separatorClassName} />
              {visibleItems.map((item, index) => (
                <Fragment key={`${item.segment}-${index}`}>
                  <BreadcrumbItem className="min-w-0">
                    {item.href ? (
                      <BreadcrumbLink asChild className={linkClassName}>
                        <Link to={item.href}>{item.label}</Link>
                      </BreadcrumbLink>
                    ) : (
                      <BreadcrumbPage className={pageClassName}>
                        {item.label}
                      </BreadcrumbPage>
                    )}
                  </BreadcrumbItem>
                  {index < visibleItems.length - 1 ? (
                    <BreadcrumbSeparator className={separatorClassName} />
                  ) : null}
                </Fragment>
              ))}
            </BreadcrumbList>
          </div>
        )}
        <div
          data-slot="desktop-breadcrumbs"
          className={cn("w-full", shouldCollapse && "hidden md:block")}
        >
          <BreadcrumbList className={`${listClassName} flex`}>
            {breadcrumbItems.map((item, index) => (
              <Fragment key={`${item.segment}-${index}`}>
                <BreadcrumbItem className="min-w-0">
                  {item.href ? (
                    <BreadcrumbLink asChild className={linkClassName}>
                      <Link to={item.href}>{item.label}</Link>
                    </BreadcrumbLink>
                  ) : (
                    <BreadcrumbPage className={pageClassName}>
                      {item.label}
                    </BreadcrumbPage>
                  )}
                </BreadcrumbItem>
                {index < breadcrumbItems.length - 1 ? (
                  <BreadcrumbSeparator className={separatorClassName} />
                ) : null}
              </Fragment>
            ))}
          </BreadcrumbList>
        </div>
      </Breadcrumb>
    </div>
  );
}
